import { z } from 'zod';

const emptyToUndefined = (val: unknown) => {
  if (val === null || val === undefined) return undefined;
  if (typeof val === 'string' && val.trim() === '') return undefined;
  return val;
};

// Patient validation schema
export const patientSchema = z.object({
  name: z.string()
    .trim()
    .min(1, { message: "Le nom est requis" })
    .max(100, { message: "Le nom doit faire moins de 100 caractères" }),
  email: z.preprocess(
    emptyToUndefined,
    z.string().trim().email({ message: "Email invalide" }).max(255, { message: "L'email doit faire moins de 255 caractères" }).optional()
  ),
  phone: z.preprocess(
    emptyToUndefined,
    z.string().trim().regex(/^[0-9+\-\s()]*$/, { message: "Numéro de téléphone invalide" }).max(20, { message: "Le téléphone doit faire moins de 20 caractères" }).optional()
  ),
  address: z.preprocess(
    emptyToUndefined,
    z.string().trim().max(500, { message: "L'adresse doit faire moins de 500 caractères" }).optional()
  ),
  medical_history: z.preprocess(
    emptyToUndefined,
    z.string().trim().max(5000, { message: "L'historique médical doit faire moins de 5000 caractères" }).optional()
  ),
  date_of_birth: z.preprocess(emptyToUndefined, z.string().optional()),
  gender: z.preprocess(emptyToUndefined, z.string().optional()),
});

// Symptom analysis validation schema
export const symptomAnalysisSchema = z.object({
  symptoms: z.string()
    .trim()
    .min(1, { message: "Les symptômes sont requis" })
    .max(2000, { message: "Les symptômes doivent faire moins de 2000 caractères" }),
  age: z.number()
    .int({ message: "L'âge doit être un nombre entier" })
    .min(0, { message: "L'âge doit être positif" })
    .max(150, { message: "L'âge doit être inférieur à 150 ans" })
    .optional(),
  gender: z.enum(['homme', 'femme']).optional(),
  medications: z.array(
    z.string()
      .trim()
      .max(200, { message: "Le nom du médicament doit faire moins de 200 caractères" })
  ).max(50, { message: "Maximum 50 médicaments" }).optional(),
  allergies: z.array(
    z.string()
      .trim()
      .max(200, { message: "Le nom de l'allergie doit faire moins de 200 caractères" })
  ).max(50, { message: "Maximum 50 allergies" }).optional(),
});

// Treatment validation schema
export const treatmentSchema = z.object({
  title: z.string()
    .trim()
    .min(1, { message: "Le titre est requis" })
    .max(200, { message: "Le titre doit faire moins de 200 caractères" }),
  detail: z.string()
    .trim()
    .min(1, { message: "Les détails sont requis" })
    .max(2000, { message: "Les détails doivent faire moins de 2000 caractères" }),
  posology: z.string()
    .trim()
    .max(500, { message: "La posologie doit faire moins de 500 caractères" })
    .optional()
    .or(z.literal('')),
  symptom_keywords: z.array(
    z.string()
      .trim()
      .min(1)
      .max(100, { message: "Chaque mot-clé doit faire moins de 100 caractères" })
  ).min(1, { message: "Au moins un mot-clé est requis" })
    .max(50, { message: "Maximum 50 mots-clés" }),
  contraindications: z.array(
    z.string()
      .trim()
      .max(200, { message: "Chaque contre-indication doit faire moins de 200 caractères" })
  ).max(30, { message: "Maximum 30 contre-indications" }),
  treatment_type: z.string(),
  priority: z.number().int().min(1).max(3),
});

// Medication validation schema
export const medicationSchema = z.object({
  medication_name: z.string()
    .trim()
    .min(1, { message: "Le nom du médicament est requis" })
    .max(200, { message: "Le nom doit faire moins de 200 caractères" }),
  dosage: z.preprocess(
    emptyToUndefined,
    z.string().trim().max(100, { message: "Le dosage doit faire moins de 100 caractères" }).optional()
  ),
  frequency: z.preprocess(
    emptyToUndefined,
    z.string().trim().max(100, { message: "La fréquence doit faire moins de 100 caractères" }).optional()
  ),
  started_at: z.preprocess(emptyToUndefined, z.string().optional()),
});

// Allergy validation schema
export const allergySchema = z.object({
  allergen: z.string()
    .trim()
    .min(1, { message: "L'allergène est requis" })
    .max(200, { message: "Le nom doit faire moins de 200 caractères" }),
  severity: z.preprocess(emptyToUndefined, z.string().optional()),
});
